import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChartV2Horizon, ChartV2Interval } from '../types/chart-v2.types';
import type { DataExtent } from '../utils/data-extent';

/**
 * Minimum "pinned to the right edge" tolerance in milliseconds. Without
 * a floor, chunky tick deltas would still keep the auto-pan locked even
 * when the user scrolled away — but very small deltas could lose the
 * pin entirely. 1 second covers typical sub-minute streaming gaps.
 */
const FOLLOW_THRESHOLD_FLOOR_MS = 1000;

export interface UseSharedDomainOptions {
  /** `null` = custom domain; do not reset the window to a horizon preset. */
  horizon: ChartV2Horizon | null;
  /**
   * Dropping the user's window on an interval change is not cosmetic: a
   * resample swap reloads the series over a different span (intraday feeds
   * carry far less history than daily), so the previous window can land
   * almost entirely outside the new data.
   */
  interval?: ChartV2Interval;
  dataExtent: DataExtent;
  initialDomain: DataExtent;
  onDomainChange?: (range: DataExtent) => void;
}

export interface UseSharedDomainResult {
  sharedDomain: [number, number];
  /** Drop the user's window and fall back to `initialDomain`. Does not emit. */
  resetToInitialDomain: () => void;
  handleDomainChange: (range: DataExtent) => void;
  handleDomainReset: () => void;
}

/**
 * Owns the chart's currently-visible x-range.
 *
 * Only the user's pan / zoom window is state. Without one the window is
 * *derived* from `initialDomain`, so it re-derives whenever the data
 * underneath it changes — first load, asset add, resample. That is v1's
 * model: `getChartDomain` runs on every `initialiseData` and, with a
 * horizon set, always rebuilds from the current timeseries rather than
 * carrying a stored window forward. Storing the window instead means
 * every way the data can move needs its own detector effect, which is
 * how the interval swap (Z11) and the empty-chart first paint (Z12) both
 * became bugs.
 */
export const useSharedDomain = ({
  horizon,
  interval,
  dataExtent,
  initialDomain,
  onDomainChange,
}: UseSharedDomainOptions): UseSharedDomainResult => {
  const [userDomain, setUserDomain] = useState<[number, number] | null>(null);

  const initialDomainRef = useRef(initialDomain);

  initialDomainRef.current = initialDomain;

  const prevHorizonRef = useRef(horizon);
  const prevIntervalRef = useRef(interval);
  const prevDataEndRef = useRef(dataExtent.end);

  useEffect(() => {
    const horizonChanged = prevHorizonRef.current !== horizon;
    const intervalChanged = prevIntervalRef.current !== interval;

    prevHorizonRef.current = horizon;
    prevIntervalRef.current = interval;

    if (!horizonChanged && !intervalChanged) {
      return;
    }

    // Custom pan/zoom clears the horizon chip (v1). Keep the user's window —
    // but only while the series underneath it is the same. Hosts clear the
    // horizon on a resample-only swap too (v1 `changeResample` writes
    // `horizon: null`), so the interval is what separates "user panned" from
    // "the data changed out from under the window".
    if (horizon == null && !intervalChanged) {
      return;
    }

    setUserDomain(null);
  }, [horizon, interval]);

  useEffect(() => {
    const prevEnd = prevDataEndRef.current;
    const newEnd = dataExtent.end;

    prevDataEndRef.current = newEnd;

    if (newEnd <= prevEnd) {
      return;
    }

    const delta = newEnd - prevEnd;
    const followThreshold = Math.max(delta * 2, FOLLOW_THRESHOLD_FLOOR_MS);

    // Only a user window can fall behind a new tick. The derived one is
    // pinned to `dataExtent.end` by `horizonToDomain`, so it follows on
    // its own.
    setUserDomain(current => {
      if (!current) {
        return current;
      }

      const wasFollowing = current[1] >= prevEnd - followThreshold;

      if (!wasFollowing) {
        return current;
      }

      return [current[0] + delta, current[1] + delta];
    });
  }, [dataExtent.end]);

  // Memoized on the bounds, not on `initialDomain` — that object is rebuilt
  // on every live tick, and `ChartV2` keys its axis-label measurement off
  // this array's identity.
  const sharedDomain = useMemo<[number, number]>(
    () => userDomain ?? [initialDomain.start, initialDomain.end],
    [userDomain, initialDomain.start, initialDomain.end]
  );

  const resetToInitialDomain = useCallback(() => setUserDomain(null), []);

  const handleDomainChange = useCallback(
    (range: DataExtent) => {
      setUserDomain([range.start, range.end]);
      onDomainChange?.(range);
    },
    [onDomainChange]
  );

  const handleDomainReset = useCallback(() => {
    const ref = initialDomainRef.current;

    setUserDomain(null);
    onDomainChange?.({ start: ref.start, end: ref.end });
  }, [onDomainChange]);

  return {
    sharedDomain,
    resetToInitialDomain,
    handleDomainChange,
    handleDomainReset,
  };
};
